import React, { useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../store/authStore';
import { useTheme } from '../../theme';
import { AnimatedIcon } from '../../components/icons';
import { TermsAcceptanceWeb } from '../../components/TermsAcceptance';
import { TERMS_VERSION } from '../../constants/terms';
import { termsService } from '../../services/termsService';
import type { QualificationType } from '../../services/authService';

interface FileRowProps {
  label: string;
  hint: string;
  file: File | null;
  required?: boolean;
  accept?: string;
  onChange: (f: File) => void;
}

const FileRow: React.FC<FileRowProps> = ({ label, hint, file, required = true, accept = 'image/*,.pdf', onChange }) => {
  const ref = useRef<HTMLInputElement>(null);
  const previewUrl = file && file.type.startsWith('image/') ? URL.createObjectURL(file) : null;
  return (
    <div style={frStyles.wrap}>
      {previewUrl ? (
        <img src={previewUrl} alt="preview" style={frStyles.thumb} />
      ) : (
        <div style={frStyles.thumbPlaceholder}>📎</div>
      )}
      <div style={frStyles.info}>
        <span style={frStyles.label}>
          {label}
          {required && <span style={frStyles.req}> *</span>}
        </span>
        <span style={frStyles.hint}>{file ? `✓ ${file.name}` : hint}</span>
      </div>
      <button
        type="button"
        style={{
          ...frStyles.btn,
          background: file ? 'var(--sc-success-bg)' : 'transparent',
          color: file ? 'var(--sc-success-text)' : 'var(--sc-brand-primary)',
          borderColor: file ? 'var(--sc-success)' : 'var(--sc-brand-primary)',
        }}
        onClick={() => ref.current?.click()}
      >
        {file ? `✓ Change` : 'Choose File'}
      </button>
      <input
        ref={ref}
        type="file"
        accept={accept}
        style={{ display: 'none' }}
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) onChange(f);
        }}
      />
    </div>
  );
};

const frStyles: Record<string, React.CSSProperties> = {
  wrap: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: '12px 14px',
    border: '1px solid var(--sc-border)',
    borderRadius: 'var(--sc-radius-md)',
    marginTop: 12,
    background: 'var(--sc-bg-input)',
  },
  thumb: {
    width: 52,
    height: 52,
    borderRadius: 8,
    objectFit: 'cover',
    border: '1px solid var(--sc-border)',
    flexShrink: 0,
  },
  thumbPlaceholder: {
    width: 52,
    height: 52,
    borderRadius: 8,
    background: 'var(--sc-bg-secondary)',
    border: '1px dashed var(--sc-border)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 20,
    flexShrink: 0,
  },
  info: { display: 'flex', flexDirection: 'column', flex: 1 },
  label: { fontSize: 13, fontWeight: 600, color: 'var(--sc-text-primary)' },
  req: { color: 'var(--sc-error)' },
  hint: { fontSize: 11, color: 'var(--sc-text-tertiary)', marginTop: 2 },
  btn: {
    whiteSpace: 'nowrap',
    padding: '6px 14px',
    borderRadius: 'var(--sc-radius-xs)',
    border: '1px solid',
    fontSize: 12,
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: 'inherit',
    transition: 'all var(--sc-transition-fast)',
  },
};

export const RegisterScreen: React.FC = () => {
  const [step, setStep] = useState(1);
  const [showPwd, setShowPwd] = useState(false);
  const [showConfirmPwd, setShowConfirmPwd] = useState(false);
  const [form, setForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '+263',
    password: '',
    confirmPassword: '',
    role: 'CLIENT' as 'CLIENT' | 'SURVEYOR',
  });

  const [idDocument, setIdDocument] = useState<File | null>(null);
  const [selfieWithId, setSelfieWithId] = useState<File | null>(null);

  const [qualificationType, setQualificationType] = useState<QualificationType>('tertiary');
  const [interviewRequested, setInterviewRequested] = useState(false);
  const [contactPhone, setContactPhone] = useState('+263');
  const [tertiaryCertificate, setTertiaryCertificate] = useState<File | null>(null);
  const [resumeCv, setResumeCv] = useState<File | null>(null);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const { register, isLoading, error, clearError } = useAuthStore();
  const navigate = useNavigate();
  const [localError, setLocalError] = useState('');

  const set = (key: string, val: string) => setForm((f) => ({ ...f, [key]: val }));
  const displayError = localError || error;
  const isSurveyor = form.role === 'SURVEYOR';
  const totalSteps = isSurveyor ? 3 : 2;

  const validateStep1 = (): string | null => {
    if (!form.first_name.trim() || !form.last_name.trim()) return 'Please enter your full name.';
    if (!/^\S+@\S+\.\S+$/.test(form.email.trim())) return 'Please enter a valid email address for OTP verification.';
    if (!form.phone.trim() || form.phone.length < 9) return 'Please enter a valid Zimbabwe phone number.';
    if (form.password.length < 8) return 'Password must be at least 8 characters.';
    if (form.password !== form.confirmPassword) return 'Passwords do not match.';
    return null;
  };

  const validateStep2 = (): string | null => {
    if (!idDocument) return 'Please upload your National ID or Passport.';
    if (!selfieWithId) return 'Please upload a selfie holding your ID/Passport.';
    return null;
  };

  const validateStep3 = (): string | null => {
    if (qualificationType === 'non_tertiary' && interviewRequested) {
      if (!contactPhone.trim() || contactPhone.length < 9) return 'Please enter a contact phone number for your interview.';
    } else {
      if (!tertiaryCertificate) return 'Please upload your tertiary certificate.';
      if (!resumeCv) return 'Please upload your resume/CV.';
    }
    if (!termsAccepted) return 'You must accept the Terms & Conditions to register.';
    return null;
  };

  const handleNext = (e: React.FormEvent) => {
    e.preventDefault();
    clearError();
    setLocalError('');
    if (step === 1) {
      const err = validateStep1();
      if (err) { setLocalError(err); return; }
      setStep(2);
    } else if (step === 2) {
      const err = validateStep2();
      if (err) { setLocalError(err); return; }
      if (isSurveyor) {
        setStep(3);
      } else {
        if (!termsAccepted) { setLocalError('You must accept the Terms & Conditions to register.'); return; }
        handleFinalSubmit();
      }
    } else if (step === 3) {
      const err = validateStep3();
      if (err) { setLocalError(err); return; }
      handleFinalSubmit();
    }
  };

  const handleFinalSubmit = async () => {
    if (!isSurveyor && !termsAccepted) {
      setLocalError('You must accept the Terms & Conditions to register.');
      return;
    }
    try {
      const isInterviewPath = isSurveyor && qualificationType === 'non_tertiary' && interviewRequested;
      const termsResp = await termsService.get().catch(() => ({ version: TERMS_VERSION, content: '' }));
      await register({
        first_name: form.first_name,
        last_name: form.last_name,
        email: form.email.trim(),
        phone: form.phone,
        password: form.password,
        role: form.role,
        terms_accepted: termsAccepted,
        terms_version: termsResp?.version || TERMS_VERSION,
        identity_docs: {
          id_document: idDocument!,
          selfie_with_id: selfieWithId!,
        },
        ...(isSurveyor ? {
          qualification_type: qualificationType,
          interview_requested: isInterviewPath,
          ...(isInterviewPath ? { contact_phone: contactPhone.trim() } : {}),
          ...(!isInterviewPath && tertiaryCertificate && resumeCv ? {
            surveyor_docs: {
              tertiary_certificate: tertiaryCertificate,
              resume_cv: resumeCv,
            },
          } : {}),
        } : {}),
      });
      navigate('/verify-otp', {
        state: {
          phone: form.phone,
          email: form.email.trim(),
          pendingInterview: isInterviewPath,
        },
      });
    } catch { }
  };

  return (
    <div style={styles.page}>
      <div style={styles.card} className="sc-animate-scale-in">
        <h2 style={styles.title}>Create account</h2>
        <p style={styles.subtitle}>
          {step === 1 && 'Join the Surveyor\'s Club marketplace'}
          {step === 2 && 'Verify identity to ensure platform trust'}
          {step === 3 && 'Professional Credentials — for surveyors'}
        </p>

        {/* Step progress bar */}
        <div style={styles.stepBar}>
          {Array.from({ length: totalSteps }, (_, i) => i + 1).map((s) => (
            <React.Fragment key={s}>
              <div style={{
                ...styles.stepDot,
                background: s < step ? 'var(--sc-success)' : s === step ? 'var(--sc-brand-primary)' : 'var(--sc-bg-input)',
                color: s <= step ? 'var(--sc-text-inverse)' : 'var(--sc-text-tertiary)',
                border: s <= step ? 'none' : '1px solid var(--sc-border)',
              }}>
                {s < step ? '✓' : s}
              </div>
              {s < totalSteps && <div style={{
                ...styles.stepLine,
                background: s < step ? 'var(--sc-success)' : 'var(--sc-border)',
              }} />}
            </React.Fragment>
          ))}
        </div>

        {displayError && (
          <div style={styles.errorBox} className="sc-animate-toast-in">
            <AnimatedIcon name="warning" size={16} color="var(--sc-error)" />
            <span style={{ fontSize: 13, fontWeight: 500 }}>{displayError}</span>
          </div>
        )}

        <form onSubmit={handleNext} style={styles.form}>
          {/* STEP 1: Personal Details */}
          {step === 1 && (
            <>
              <div style={styles.row}>
                <div style={styles.col}>
                  <label style={styles.label}>First Name</label>
                  <input
                    value={form.first_name}
                    onChange={(e) => set('first_name', e.target.value)}
                    style={styles.input}
                    placeholder="Tendai"
                    required
                  />
                </div>
                <div style={styles.col}>
                  <label style={styles.label}>Last Name</label>
                  <input
                    value={form.last_name}
                    onChange={(e) => set('last_name', e.target.value)}
                    style={styles.input}
                    placeholder="Moyo"
                    required
                  />
                </div>
              </div>

              <div style={styles.col}>
                <label style={styles.label}>Phone Number</label>
                <input
                  type="tel"
                  value={form.phone}
                  onChange={(e) => set('phone', e.target.value)}
                  placeholder="+263 77 123 4567"
                  style={styles.input}
                  required
                />
              </div>

              <div style={styles.col}>
                <label style={styles.label}>Email Address</label>
                <input
                  type="email"
                  value={form.email}
                  onChange={(e) => set('email', e.target.value)}
                  placeholder="you@example.com"
                  style={styles.input}
                  required
                />
              </div>

              <div style={styles.col}>
                <label style={styles.label}>Account Status</label>
                <div style={styles.roleRow}>
                  {(['CLIENT', 'SURVEYOR'] as const).map((role) => (
                    <button
                      key={role}
                      type="button"
                      style={{
                        ...styles.roleBtn,
                        background: form.role === role ? 'var(--sc-bg-active)' : 'var(--sc-bg-input)',
                        borderColor: form.role === role ? 'var(--sc-brand-primary)' : 'var(--sc-border)',
                        color: form.role === role ? 'var(--sc-brand-primary)' : 'var(--sc-text-secondary)',
                        fontWeight: form.role === role ? 700 : 500,
                      }}
                      onClick={() => {
                        set('role', role);
                        if (role !== form.role) {
                          setQualificationType('tertiary');
                          setInterviewRequested(false);
                          setContactPhone('+263');
                          setTertiaryCertificate(null);
                          setResumeCv(null);
                        }
                      }}
                    >
                      {role === 'CLIENT' ? '👤 Client' : '👷 Surveyor'}
                    </button>
                  ))}
                </div>
              </div>

              <div style={styles.col}>
                <label style={styles.label}>Password</label>
                <div style={styles.inputWrapper}>
                  <input
                    type={showPwd ? 'text' : 'password'}
                    value={form.password}
                    onChange={(e) => set('password', e.target.value)}
                    style={{ ...styles.input, paddingRight: 48 }}
                    placeholder="At least 8 characters"
                    minLength={8}
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPwd(!showPwd)}
                    style={styles.showPwdBtn}
                  >
                    <AnimatedIcon name={showPwd ? 'eye-off' : 'eye'} size={16} color="var(--sc-text-secondary)" />
                  </button>
                </div>
              </div>

              <div style={styles.col}>
                <label style={styles.label}>Confirm Password</label>
                <div style={styles.inputWrapper}>
                  <input
                    type={showConfirmPwd ? 'text' : 'password'}
                    value={form.confirmPassword}
                    onChange={(e) => set('confirmPassword', e.target.value)}
                    style={{ ...styles.input, paddingRight: 48 }}
                    placeholder="Repeat your password"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPwd(!showConfirmPwd)}
                    style={styles.showPwdBtn}
                  >
                    <AnimatedIcon name={showConfirmPwd ? 'eye-off' : 'eye'} size={16} color="var(--sc-text-secondary)" />
                  </button>
                </div>
              </div>

              {!isSurveyor && <TermsAcceptanceWeb accepted={termsAccepted} onChange={setTermsAccepted} />}
            </>
          )}

          {/* STEP 2: Identity Verification */}
          {step === 2 && (
            <>
              <div style={styles.infoBox}>
                <AnimatedIcon name="shield" size={16} color="var(--sc-success-text)" />
                <span style={{ fontSize: 13, lineHeight: 1.5, marginLeft: 8 }}>
                  <strong>Verify Identity:</strong> Verification details help secure transactions and confirm identities.
                </span>
              </div>

              <FileRow
                label="National ID or Passport"
                hint="JPG, PNG, PDF, DOC or DOCX — max 10 MB"
                file={idDocument}
                accept="image/*,.pdf,.doc,.docx"
                onChange={setIdDocument}
              />
              <FileRow
                label="Selfie holding document"
                hint="Must clearly show your face and ID details"
                file={selfieWithId}
                accept="image/*"
                onChange={setSelfieWithId}
              />

              <p style={styles.privacyNote}>
                🔒 Your security matters. All data is encrypted and used exclusively for compliance reviews.
              </p>
            </>
          )}

          {/* STEP 3: Surveyor Professional Docs */}
          {step === 3 && (
            <>
              <div style={styles.col}>
                <label style={styles.label}>Qualification Type</label>
                <div style={styles.roleRow}>
                  {[
                    { key: 'tertiary' as const, label: 'Tertiary Qualified' },
                    { key: 'non_tertiary' as const, label: 'Non-Tertiary Academics' },
                  ].map((q) => (
                    <button
                      key={q.key}
                      type="button"
                      style={{
                        ...styles.roleBtn,
                        borderColor: qualificationType === q.key ? 'var(--sc-brand-primary)' : 'var(--sc-border)',
                        background: qualificationType === q.key ? 'var(--sc-brand-primary-10)' : 'var(--sc-bg-input)',
                        color: qualificationType === q.key ? 'var(--sc-brand-primary)' : 'var(--sc-text-secondary)',
                      }}
                      onClick={() => {
                        setQualificationType(q.key);
                        setInterviewRequested(q.key === 'non_tertiary');
                        if (q.key === 'tertiary') {
                          setContactPhone('+263');
                        } else {
                          setTertiaryCertificate(null);
                          setResumeCv(null);
                        }
                      }}
                    >
                      {q.label}
                    </button>
                  ))}
                </div>
              </div>

              {qualificationType === 'non_tertiary' && interviewRequested ? (
                <>
                  <div style={styles.infoBox}>
                    <AnimatedIcon name="worker" size={16} color="var(--sc-success-text)" />
                    <span style={{ fontSize: 13, lineHeight: 1.5, marginLeft: 8 }}>
                      <strong>Interview Request:</strong> An admin will contact you to schedule an interview before your account is approved.
                    </span>
                  </div>
                  <div style={styles.col}>
                    <label style={styles.label}>Contact Phone *</label>
                    <input
                      type="tel"
                      value={contactPhone}
                      onChange={(e) => setContactPhone(e.target.value)}
                      style={styles.input}
                      placeholder="+263 77 123 4567"
                      required
                    />
                  </div>
                </>
              ) : (
                <>
                  <div style={styles.infoBox}>
                    <AnimatedIcon name="worker" size={16} color="var(--sc-success-text)" />
                    <span style={{ fontSize: 13, lineHeight: 1.5, marginLeft: 8 }}>
                      <strong>Qualification Review:</strong> Surveyor accounts stay pending until admin approves these documents.
                    </span>
                  </div>
                  <FileRow
                    label="Tertiary Certificate"
                    hint="Diploma, degree, qualification certificate, PDF, DOC, DOCX or image"
                    file={tertiaryCertificate}
                    accept="image/*,.pdf,.doc,.docx"
                    onChange={setTertiaryCertificate}
                  />
                  <FileRow
                    label="Resume / CV"
                    hint="PDF, DOC, DOCX or image"
                    file={resumeCv}
                    accept="image/*,.pdf,.doc,.docx"
                    onChange={setResumeCv}
                  />
                </>
              )}

              <TermsAcceptanceWeb accepted={termsAccepted} onChange={setTermsAccepted} />
            </>
          )}

          <div style={styles.btnRow}>
            {step > 1 && (
              <button
                type="button"
                style={styles.backBtn}
                onClick={() => {
                  setLocalError('');
                  clearError();
                  setStep((s) => s - 1);
                }}
                className="sc-press"
              >
                Back
              </button>
            )}
            <button
              type="submit"
              style={{
                ...styles.submitBtn,
                flex: step > 1 ? 'unset' : 1,
              }}
              disabled={isLoading}
              className="sc-press"
            >
              {isLoading
                ? 'Submitting...'
                : step < totalSteps
                  ? 'Continue'
                  : 'Submit Application'}
            </button>
          </div>
        </form>

        {/* Social Login */}
        <div style={styles.socialDivider}>
          <div style={{ ...styles.dividerLine, background: 'var(--sc-border-light)' }} />
          <span style={{ ...styles.dividerText, fontWeight: 600, fontSize: 11, letterSpacing: '0.5px' }}>or register with</span>
          <div style={{ ...styles.dividerLine, background: 'var(--sc-border-light)' }} />
        </div>
        <div style={styles.socialRow}>
          {[
            { label: 'Google', icon: 'google' as const, bg: '#FFF', text: '#3C4043', border: '#DADCE0' },
            { label: 'Facebook', icon: 'facebook' as const, bg: '#1877F2', text: '#FFF', border: '#1877F2' },
            { label: 'WhatsApp', icon: 'whatsapp' as const, bg: '#25D366', text: '#FFF', border: '#25D366' },
          ].map(s => (
            <button
              key={s.label}
              type="button"
              disabled
              style={{ ...styles.socialBtn, backgroundColor: '#E5E7EB', color: '#6B7280', borderColor: '#D1D5DB', cursor: 'not-allowed', opacity: 0.65 }}
            >
              <AnimatedIcon name={s.icon} size={16} color="#6B7280" />
              <span style={{ fontSize: 12, fontWeight: 700 }}>{s.label}</span>
            </button>
          ))}
        </div>

        <p style={styles.footerText}>
          Already have an account?{' '}
          <Link to="/login" style={styles.link}>Sign In</Link>
        </p>
      </div>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  page: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100vh',
    background: 'var(--sc-bg-primary)',
    fontFamily: 'var(--sc-font-family)',
    padding: 16,
  },
  card: {
    background: 'var(--sc-bg-secondary)',
    borderRadius: 'var(--sc-radius-xl)',
    padding: '40px 32px',
    width: '100%',
    maxWidth: 480,
    boxShadow: 'var(--sc-shadow-xl)',
    border: '1px solid var(--sc-border-light)',
    boxSizing: 'border-box',
  },
  title: {
    fontSize: 22,
    fontWeight: 800,
    color: 'var(--sc-text-primary)',
    textAlign: 'center',
    margin: '0 0 6px',
    fontFamily: 'var(--sc-font-display)',
    letterSpacing: '-0.5px',
  },
  subtitle: {
    fontSize: 14,
    color: 'var(--sc-text-secondary)',
    textAlign: 'center',
    margin: '0 0 24px',
  },
  stepBar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 28,
  },
  stepDot: {
    width: 32,
    height: 32,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 12,
    fontWeight: 700,
    flexShrink: 0,
    transition: 'all var(--sc-transition-fast)',
  },
  stepLine: {
    flex: 1,
    height: 2,
    margin: '0 8px',
    transition: 'background var(--sc-transition-fast)',
  },
  errorBox: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    background: 'var(--sc-error-bg)',
    border: '1px solid var(--sc-error)',
    borderRadius: 'var(--sc-radius-md)',
    padding: '12px 16px',
    color: 'var(--sc-error-text)',
    marginBottom: 20,
  },
  infoBox: {
    display: 'flex',
    alignItems: 'center',
    background: 'var(--sc-success-bg)',
    border: '1px solid var(--sc-success)',
    borderRadius: 'var(--sc-radius-md)',
    padding: '12px 16px',
    color: 'var(--sc-success-text)',
    marginBottom: 16,
  },
  privacyNote: {
    fontSize: 12,
    color: 'var(--sc-text-tertiary)',
    marginTop: 16,
    lineHeight: 1.5,
    textAlign: 'center',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: 14,
  },
  row: {
    display: 'flex',
    gap: 12,
  },
  col: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
  },
  label: {
    fontSize: 13,
    fontWeight: 600,
    color: 'var(--sc-text-secondary)',
    marginTop: 4,
  },
  input: {
    width: '100%',
    height: 'var(--sc-touch-input)',
    padding: '0 16px',
    fontSize: 14,
    fontFamily: 'inherit',
    color: 'var(--sc-text-primary)',
    background: 'var(--sc-bg-input)',
    border: '1px solid var(--sc-border)',
    borderRadius: 'var(--sc-radius-md)',
    outline: 'none',
    boxSizing: 'border-box',
    transition: 'all var(--sc-transition-fast)',
  },
  roleRow: {
    display: 'flex',
    gap: 12,
  },
  roleBtn: {
    flex: 1,
    height: 'var(--sc-touch-input)',
    borderRadius: 'var(--sc-radius-md)',
    border: '1px solid',
    cursor: 'pointer',
    fontFamily: 'inherit',
    fontSize: 14,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    transition: 'all var(--sc-transition-fast)',
  },
  btnRow: {
    display: 'flex',
    gap: 12,
    marginTop: 20,
  },
  backBtn: {
    height: 'var(--sc-touch-btn)',
    padding: '0 24px',
    background: 'transparent',
    border: '1px solid var(--sc-border)',
    borderRadius: 'var(--sc-radius-md)',
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
    color: 'var(--sc-text-secondary)',
    fontFamily: 'inherit',
    transition: 'all var(--sc-transition-fast)',
  },
  submitBtn: {
    height: 'var(--sc-touch-btn)',
    padding: '0 28px',
    background: 'var(--sc-brand-gradient)',
    color: 'var(--sc-text-on-brand)',
    border: 'none',
    borderRadius: 'var(--sc-radius-md)',
    fontSize: 14,
    fontWeight: 700,
    cursor: 'pointer',
    fontFamily: 'inherit',
    boxShadow: 'var(--sc-shadow-brand)',
    transition: 'all var(--sc-transition-fast)',
  },
  footerText: {
    textAlign: 'center',
    fontSize: 13,
    color: 'var(--sc-text-secondary)',
    marginTop: 24,
  },
  link: {
    color: 'var(--sc-brand-primary)',
    fontWeight: 700,
    textDecoration: 'none',
  },
  inputWrapper: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    width: '100%',
  },
  showPwdBtn: {
    position: 'absolute',
    right: 4,
    height: 40,
    width: 40,
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  socialDivider: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    margin: '20px 0 14px',
  },
  dividerLine: {
    flex: 1,
    height: 1,
  },
  dividerText: {
    fontSize: 11,
    color: 'var(--sc-text-tertiary)',
  },
  socialRow: {
    display: 'flex',
    gap: 8,
  },
  socialBtn: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    height: 42,
    border: '1px solid',
    borderRadius: 'var(--sc-radius-md)',
    cursor: 'pointer',
    fontFamily: 'inherit',
    transition: 'opacity 0.15s',
  },
};
